// SMS Provider (Twilio / Quo) + SendGrid Email — the two "outbound channels"
// integration blocks lifted out of Settings.jsx to keep hot-reload snappy.
import { toast } from "sonner";
import {
  MessageSquare, MailIcon, CheckCircle2, Loader2, Save, Send,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Section, Field } from "./common";

export default function IntegrationsSection({
  form, set, save, saving, settings, secretMeta,
  testSmsPhone, setTestSmsPhone, runTestSms, testingSms,
  testEmail, setTestEmail, runTestEmail, testingEmail,
}) {
  return (
    <>
      {/* ──────────────── SMS Provider (Twilio vs Quo) ──────────────── */}
      <Section
        title="SMS Provider"
        subtitle="Outbound campaign SMS routes through whichever provider you pick here. Vapi voice calls always use Vapi's managed Twilio number regardless of this setting."
        icon={<MessageSquare className="w-4 h-4" />}
      >
        {form.sms_provider === "quo" && (
          <div
            className="mb-4 border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 flex items-start gap-2"
            data-testid="twilio-killswitch-banner"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div>
              <span className="font-bold uppercase tracking-wider">Twilio disabled for SMS.</span>{" "}
              Outbound SMS routes through Quo. Any inbound Twilio webhook to this workspace
              is silently ignored — replies are only ingested from the Quo signed webhook below.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5" data-testid="sms-provider-grid">
          {[
            { value: "twilio", label: "Twilio", desc: `Account: ${settings?.twilio_phone_number || "—"}`, configured: settings?.twilio_configured },
            { value: "quo",    label: "Quo.com", desc: settings?.quo_configured ? "Configured" : "Add API key + phone below", configured: settings?.quo_configured },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`border p-4 cursor-pointer transition-colors ${
                form.sms_provider === opt.value ? "border-[#002FA7] bg-blue-50/40" : "border-zinc-200 hover:border-zinc-400"
              }`}
              data-testid={`sms-provider-${opt.value}`}
            >
              <input
                type="radio" name="sms_provider" value={opt.value}
                checked={form.sms_provider === opt.value}
                onChange={() => set("sms_provider", opt.value)}
                className="sr-only"
              />
              <div className="flex items-center justify-between">
                <div className="text-sm font-bold uppercase tracking-wider">{opt.label}</div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${opt.configured ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                  {opt.configured ? "Ready" : "Setup"}
                </span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{opt.desc}</div>
            </label>
          ))}
        </div>

        {form.sms_provider === "quo" && (
          <div className="space-y-3 mb-5" data-testid="quo-config-block">
            <Field label="Quo API key" hint="Get it from quo.com → Settings → API. Stored encrypted at rest.">
              <input
                type="password"
                value={form.quo_api_key}
                onChange={(e) => set("quo_api_key", e.target.value)}
                data-testid="input-quo-api-key"
                placeholder={secretMeta.quo_api_key?.set ? `Saved · ••••${secretMeta.quo_api_key.last4} (type to replace)` : "qopk_…"}
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
              />
            </Field>
            <Field label="Quo phone number" hint="E.164 (e.g. +17131234567) or a Quo phone-number ID (PN…).">
              <input
                type="text"
                value={form.quo_phone_number}
                onChange={(e) => set("quo_phone_number", e.target.value)}
                data-testid="input-quo-phone"
                placeholder="+17131234567"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
              />
            </Field>
            <Field label="Quo webhook secret" hint={`Paste the "whsec_…" value from your Quo webhook settings. Used to verify inbound replies are really from Quo.`}>
              <input
                type="password"
                value={form.quo_webhook_secret}
                onChange={(e) => set("quo_webhook_secret", e.target.value)}
                data-testid="input-quo-webhook-secret"
                placeholder={secretMeta.quo_webhook_secret?.set ? `Saved · ••••${secretMeta.quo_webhook_secret.last4} (type to replace)` : "whsec_…"}
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
              />
            </Field>
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs">
              <div className="font-bold uppercase tracking-wider text-zinc-700 mb-1">Webhook URL (paste in Quo dashboard)</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-2 py-1.5 bg-white border border-zinc-200 text-zinc-800 break-all" data-testid="quo-webhook-url">
                  {settings?.quo_webhook_url || ""}
                </code>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(settings?.quo_webhook_url || ""); toast.success("Copied"); }}
                  className="px-3 py-1.5 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-100"
                  data-testid="btn-copy-quo-webhook"
                >Copy</button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/settings/test-quo-webhook");
                      if (data.hint) {
                        toast.warning(data.hint, { duration: 8000 });
                      } else if (data.signature_verified && data.looks_like_real_quo_secret) {
                        toast.success(`Quo secret is healthy (${data.decoded_secret_len} bytes decoded). Send a test SMS to confirm end-to-end.`, { duration: 6000 });
                      } else {
                        toast.error("HMAC self-test failed — please report this.");
                      }
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail) || "Test failed.");
                    }
                  }}
                  className="px-3 py-1.5 border border-[#002FA7] text-[#002FA7] text-[10px] font-bold uppercase tracking-wider hover:bg-blue-50"
                  data-testid="btn-test-quo-webhook"
                >Test secret</button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">
                <strong>Reply not appearing on a lead?</strong> Click <em>Test secret</em>. If it fails, copy the latest
                signing key from Quo dashboard → Webhooks → Signing Key, paste above and Save provider.
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pt-2">
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="btn-save-sms-provider"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save provider
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="tel"
              value={testSmsPhone}
              onChange={(e) => setTestSmsPhone(e.target.value)}
              placeholder="+17131234567"
              data-testid="input-test-sms-phone"
              className="px-3 py-2 border border-zinc-300 text-sm w-48 font-mono"
            />
            <button
              type="button" onClick={runTestSms} disabled={testingSms}
              data-testid="btn-test-sms"
              className="bg-zinc-900 hover:bg-black text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {testingSms ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send test SMS
            </button>
          </div>
        </div>
      </Section>

      {/* ──────────────── SendGrid Email ──────────────── */}
      <Section
        title="SendGrid Email"
        subtitle="Add 'email' as a sequence step to send personalized emails alongside SMS and calls. Replies arrive in the Lead timeline via Inbound Parse."
        icon={<MailIcon className="w-4 h-4" />}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs text-zinc-500">
            Status:{" "}
            <span className={`font-bold ${settings?.sendgrid_configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="sendgrid-status">
              {settings?.sendgrid_configured ? "Connected" : "Not configured"}
            </span>
          </div>
        </div>

        <div className="space-y-3 mb-5">
          <Field label="SendGrid API key" hint="Leave blank to use the global key. Create a restricted key with 'Mail Send' + 'Inbound Parse' scope.">
            <input
              type="password"
              value={form.sendgrid_api_key}
              onChange={(e) => set("sendgrid_api_key", e.target.value)}
              data-testid="input-sendgrid-api-key"
              placeholder={secretMeta.sendgrid_api_key?.set ? `Saved · ••••${secretMeta.sendgrid_api_key.last4} (type to replace)` : "SG.…"}
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7] font-mono"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="From email" hint="Must be on a SendGrid-authenticated domain.">
              <input
                type="email"
                value={form.sendgrid_from_email}
                onChange={(e) => set("sendgrid_from_email", e.target.value)}
                data-testid="input-sendgrid-from-email"
                placeholder="no-reply@scalater.com"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </Field>
            <Field label="From name">
              <input
                type="text"
                value={form.sendgrid_from_name}
                onChange={(e) => set("sendgrid_from_name", e.target.value)}
                data-testid="input-sendgrid-from-name"
                placeholder="Sara from Scalater"
                className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
              />
            </Field>
          </div>
          <Field label="Reply-to (optional)" hint="If set, replies go here. Leave blank to use From email.">
            <input
              type="email"
              value={form.sendgrid_reply_to}
              onChange={(e) => set("sendgrid_reply_to", e.target.value)}
              data-testid="input-sendgrid-reply-to"
              placeholder="sales@scalater.com"
              className="w-full px-3 py-2.5 border border-zinc-300 text-sm focus:outline-none focus:border-[#002FA7]"
            />
          </Field>
        </div>

        <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs mb-5">
          <div className="font-bold uppercase tracking-wider text-zinc-700 mb-1">Inbound Parse URL (paste in SendGrid → Settings → Inbound Parse)</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-2 py-1.5 bg-white border border-zinc-200 text-zinc-800 break-all" data-testid="sendgrid-inbound-url">
              {settings?.sendgrid_inbound_url || ""}
            </code>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(settings?.sendgrid_inbound_url || ""); toast.success("Copied"); }}
              className="px-3 py-1.5 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-100"
              data-testid="btn-copy-sendgrid-inbound"
            >Copy</button>
          </div>
          <div className="text-zinc-500 mt-2">
            Point an MX record (e.g. <code>reply.scalater.com</code> → <code>mx.sendgrid.net</code>) and bind it to this URL. Replies become inbound activities on the matching lead.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button" onClick={save} disabled={saving}
            data-testid="btn-save-sendgrid"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save email settings
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="input-test-email"
              className="px-3 py-2 border border-zinc-300 text-sm w-64"
            />
            <button
              type="button" onClick={runTestEmail} disabled={testingEmail}
              data-testid="btn-test-email"
              className="bg-zinc-900 hover:bg-black text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              {testingEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send test email
            </button>
          </div>
        </div>
      </Section>
    </>
  );
}
